import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "motion/react";
import ServiceLocator from "../../../../../domain/service-locator";
import { BackIcon } from "../../../../components/layout/keypad/back-icon";

const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, undefined, 0, undefined];

const AREA_COLORS = ["#EF834A", "#84B4A8", "#8697C1"];
const INACTIVE_COLOR = "#CCCCCC";

interface MinpakuKeypadProps {
  onComplete: () => void;
}

export const MinpakuKeypad = (props: MinpakuKeypadProps) => {
  const theme = ServiceLocator.ThemeStore.theme;
  const guidePlayerModule = ServiceLocator.GuidePlayerModule;
  const playerLayoutModule = ServiceLocator.PlayerLayoutModule;
  const shouldMarginBottom = guidePlayerModule.currentTrack.watch();
  const miniPlayerHeight =
    playerLayoutModule.playerRectInfo.watch().miniPlayerHeight;

  const localizedEntityRepository = ServiceLocator.LocalizedEntityRepository;
  const entities = localizedEntityRepository.entities.watch();

  const [selectedAreaIndex, setSelectedAreaIndex] = useState(0);
  const [num, setNum] = useState<string>("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const areas = useMemo(
    () => entities.areas.filter((area) => !area.isDefault),
    [entities.areas]
  );

  const areaGuides = useMemo(() => {
    const area = areas[selectedAreaIndex];
    if (!area) return [];
    const areaTours = entities.tours.filter((t) => t.areaId === area.id);
    return areaTours.flatMap((t) =>
      entities.guides.filter((g) => g.tourId === t.id)
    );
  }, [areas, selectedAreaIndex, entities.tours, entities.guides]);

  const handleClickOkButton = useCallback(() => {
    const index = Number(num);
    setNum("");

    const guide = areaGuides[index];
    if (!guide) {
      showToast("該当するガイドが見つかりません");
      return;
    }

    window.location.hash = guide.id;
    guidePlayerModule.select(guide.id);
    playerLayoutModule.playerType.setValue("full");
    props.onComplete();
  }, [areaGuides, guidePlayerModule, playerLayoutModule.playerType, props, num, showToast]);

  return (
    <div
      className={
        "flex-grow w-full flex flex-col gap-4 py-4 overflow-y-auto hidden-scrollbar relative"
      }
    >
      <div className="text-center text-black font-bold">
        お聴きになりたいガイドの「ブロック」と
        <br />
        「ガイド番号」を押して、OKボタンを押してください。
      </div>

      {/* エリアセレクター */}
      <AreaSelector>
        {areas.map((area, index) => (
          <AreaButton
            key={area.id}
            isActive={index === selectedAreaIndex}
            activeColor={AREA_COLORS[index % AREA_COLORS.length]}
            onClick={() => {
              setSelectedAreaIndex(index);
              setNum("");
            }}
          >
            {`${String.fromCharCode(65 + index)}ブロック`}
          </AreaButton>
        ))}
      </AreaSelector>
      {/* 番号表示 + OKボタン */}
      <div className="relative h-24 w-[80%] mx-auto flex-shrink-0">
        <div
          className="absolute inset-0 flex items-center justify-center text-[4rem] font-bold"
          style={{ color: theme.color.primary }}
        >
          {num.split("").map((x, i) => (
            <span key={i}>{x}</span>
          ))}
        </div>
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer rounded-full py-1 px-6 font-bold text-2xl"
          style={{ backgroundColor: theme.color.primary, color: "#FFFFFF" }}
          onClick={handleClickOkButton}
        >
          OK
        </div>
        {/* トースト */}
        <AnimatePresence>
          {toastMessage && (
            <motion.div
              key="toast"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 rounded-lg px-4 py-3 text-center text-white font-bold text-base shadow-lg"
              style={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
            >
              {toastMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Divider */}
      <div
        className="w-[80%] mx-auto h-[2px]"
        style={{ backgroundColor: theme.color.primary }}
      />

      {/* テンキー */}
      <div className="w-[80%] mx-auto">
        <div
          className="relative w-full grid grid-cols-3 gap-y-5"
          style={{ color: theme.color.primary }}
        >
          {numbers.map((x, i) =>
            x !== undefined ? (
              <div
                className="flex justify-center items-center text-[3rem] font-bold"
                key={`keypad-num-${x}`}
                onClick={() =>
                  setNum((y) => (y.length >= 3 ? y : y + x.toString()))
                }
              >
                {x}
              </div>
            ) : i === 11 ? (
              <div
                key="keypad-back"
                className="flex items-center justify-center pr-7 pt-2"
                onClick={() => setNum("")}
              >
                <BackIcon color={theme.color.primary} width={45} />
              </div>
            ) : (
              <div key={`keypad-empty-${i}`} />
            )
          )}
        </div>
      </div>
      {shouldMarginBottom && (
        <div
          className="w-full flex-shrink-0"
          style={{ height: miniPlayerHeight }}
        />
      )}
    </div>
  );
};

const AreaSelector = styled.div`
  display: flex;
  padding: 0 16px;
  gap: 4px;
  margin-bottom: 8px;
`;

const AreaButton = styled.button<{ isActive: boolean; activeColor: string }>`
  flex: 1;
  padding: 12px 8px;
  font-size: 14px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.isActive ? props.activeColor : INACTIVE_COLOR};
  color: ${(props) => (props.isActive ? "#FFFFFF" : "#666666")};
  box-shadow: ${(props) =>
    props.isActive
      ? "0 4px 8px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.15)"
      : "0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1)"};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25), 0 3px 6px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
`;
